"use client";

import { QueryClient } from "@tanstack/react-query";

/**
 * Create a QueryClient with default options optimized for this app.
 *
 * Default settings:
 * - staleTime: 30s - Data is fresh for 30 seconds before refetching
 * - gcTime: 5 min - Unused data stays in cache for 5 minutes
 * - retry: 3 - Retry failed requests 3 times with exponential backoff
 * - refetchOnWindowFocus: false - Don't auto-refetch when tab gains focus
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0, // Don't retry mutations
      },
    },
  });
}

/**
 * Singleton QueryClient for use in providers.
 * This ensures the same client is used across the app.
 */
let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  } else {
    // Browser: make a singleton query client
    if (!browserQueryClient) {
      browserQueryClient = createQueryClient();
    }
    return browserQueryClient;
  }
}
